import React from "react";
import { motion } from "framer-motion";
import { TOAST_TAB_BASE_URL } from "../../utils/toastTabLinks";
import {
  FaTruck,
  FaBirthdayCake,
  FaClock,
  FaBoxOpen,
  FaHeadset,
  FaMobileAlt,
} from "react-icons/fa";
import {
  DecorativeElement,
  FloatingElement,
} from "../../components/Decorative";

const Services = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 },
  };

  const services = [
    {
      icon: <FaTruck className="text-4xl" />,
      title: "Fast Home Delivery",
      description:
        "Fresh, hot meals delivered to your doorstep within 30-45 minutes. Track your order in real-time with our mobile app.",
      color: "from-[#947d25] to-[#ac9430]",
    },
    {
      icon: <FaBirthdayCake className="text-4xl" />,
      title: "Event Catering",
      description:
        "Professional catering for events of all sizes. From intimate gatherings to large celebrations with authentic Indian cuisine.",
      color: "from-[#ac9430] to-[#c4ab3d]",
    },
    {
      icon: <FaClock className="text-4xl" />,
      title: "Pre-Order & Schedule",
      description:
        "Plan ahead with our advance ordering system. Schedule deliveries for specific times that work best for you.",
      color: "from-[#947d25] to-[#ac9430]",
    },
    {
      icon: <FaBoxOpen className="text-4xl" />,
      title: "Bulk Orders",
      description:
        "Special pricing for large orders. Perfect for office lunches, parties, and corporate events with customized packaging.",
      color: "from-[#ac9430] to-[#c4ab3d]",
    },
    {
      icon: <FaMobileAlt className="text-4xl" />,
      title: "Mobile App Ordering",
      description:
        "Download our app for exclusive deals, faster checkout, and order tracking. Available on iOS and Android.",
      color: "from-[#947d25] to-[#ac9430]",
    },
    {
      icon: <FaHeadset className="text-4xl" />,
      title: "24/7 Customer Support",
      description:
        "Our dedicated support team is always ready to help. Contact us anytime for orders, inquiries, or special requests.",
      color: "from-[#ac9430] to-[#c4ab3d]",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#1a1812] via-[#2a2620] to-[#1a1812] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1601050690597-df0568f70950?w=1920&h=600&fit=crop"
            alt="Kitchen"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 bg-[#ac9430]/20 px-4 py-2 rounded-full mb-6"
          >
            <FaTruck className="text-[#ac9430]" />
            <span className="text-sm font-semibold text-[#ac9430]">
              Ghost Kitchen
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-4xl md:text-6xl font-bold mb-6"
          >
            Delivery & Catering Services
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-xl mb-8 max-w-3xl mx-auto text-gray-300"
          >
            From your home to grand celebrations, we bring authentic Indian
            flavors straight to you
          </motion.p>
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            whileHover={{ scale: 1.05 }}
            onClick={() => window.open(TOAST_TAB_BASE_URL, '_blank')}
            className="bg-gradient-to-r from-[#947d25] to-[#ac9430] hover:from-[#ac9430] hover:to-[#c4ab3d]  text-white px-8 py-3 rounded-full font-semibold shadow-lg cursor-pointer"
          >
            Order Now
          </motion.button>
        </div>
      </section>

      {/* Services Grid Section */}
      <section className="py-20 relative">
        <DecorativeElement type="corners" />
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 bg-[#ac9430]/10 px-4 py-2 rounded-full mb-4"
            >
              <span className="text-sm font-semibold text-[#ac9430]">
                What We Offer
              </span>
            </motion.div>
            <h2 className="text-4xl md:text-5xl font-bold text-headingColor mb-4">
              Our Services
            </h2>
            <p className="text-lg text-textColor max-w-2xl mx-auto">
              Comprehensive delivery and catering solutions designed for your
              convenience
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden"
                whileHover={{ y: -10 }}
              >
                <motion.div
                  className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}
                />

                <motion.div
                  className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${service.color} text-white mb-6 shadow-lg`}
                  whileHover={{ rotate: 360, scale: 1.1 }}
                  transition={{ duration: 0.6 }}
                >
                  {service.icon}
                </motion.div>

                <h3 className="text-2xl font-bold text-headingColor mb-4 group-hover:text-[#ac9430] transition-colors">
                  {service.title}
                </h3>
                <p className="text-textColor leading-relaxed">
                  {service.description}
                </p>

                <motion.div
                  className={`absolute bottom-0 left-0 h-1 bg-gradient-to-r ${service.color}`}
                  initial={{ width: 0 }}
                  whileInView={{ width: "100%" }}
                  transition={{ delay: index * 0.1 + 0.3, duration: 0.6 }}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-4xl font-bold text-headingColor">
                Why Choose Royal Curry Leaf?
              </h2>
              <div className="space-y-4">
                {[
                  {
                    title: "Quality Ingredients",
                    desc: "Fresh, premium ingredients sourced daily",
                  },
                  {
                    title: "Expert Chefs",
                    desc: "20+ years of culinary expertise",
                  },
                  {
                    title: "Fast Delivery",
                    desc: "Hot meals delivered within 30-45 minutes",
                  },
                  {
                    title: "Hygiene First",
                    desc: "Certified kitchen with highest safety standards",
                  },
                ].map((item, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className="flex items-start gap-4 p-4 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow"
                  >
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#947d25] to-[#ac9430] flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold">{idx + 1}</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-headingColor mb-1">
                        {item.title}
                      </h3>
                      <p className="text-textColor text-sm">{item.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <FloatingElement delay={0.2}>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1601050690597-df0568f70950?w=600&h=800&fit=crop"
                  alt="Kitchen"
                  className="rounded-2xl shadow-2xl w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-2xl flex items-end p-8">
                  <div className="text-white">
                    <h3 className="text-2xl font-bold mb-2">
                      Professional Kitchen
                    </h3>
                    <p className="text-sm opacity-90">Where magic happens</p>
                  </div>
                </div>
              </div>
            </FloatingElement>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#1a1812] via-[#2a2620] to-[#1a1812] text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold mb-6"
          >
            Ready to Order?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-lg mb-8 text-gray-300"
          >
            Experience authentic Indian cuisine delivered fresh to your doorstep
          </motion.p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              className="bg-gradient-to-r from-[#947d25] to-[#ac9430] hover:from-[#ac9430] hover:to-[#c4ab3d] text-white px-8 py-3 rounded-full font-semibold shadow-lg transition-all duration-300"
            >
              Order Online
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              className="bg-white text-gray-900 px-8 py-3 rounded-full font-semibold"
            >
              Call +1 (857) 605-5777
            </motion.button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
